import { supabase } from '@/lib/supabase'

// The persistent in-app notification engine (Phase 1e) — table-backed, recipient-scoped,
// with read state + dedup. Distinct from the legacy `notifications.ts`, which DERIVES
// standing alerts (bank_missing/errors/under_expected) client-side from dashboard data;
// those get re-homed onto this table in a later layer.

export type NotificationType =
  | 'correction_approved'
  | 'correction_rejected'
  | 'correction_pending'
  | 'bank_missing'
  | 'document_error'
  // A portal user asked for one of their documents to be removed (they cannot remove it
  // themselves). entity_type 'document' → the row links to that document's edit dialog.
  | 'document_deletion_requested'
  // A portal user says a document was mis-catalogued and wants us to look at it.
  | 'document_question_asked'

export type NotificationRow = {
  id: string
  type: string
  title: string
  body: string | null
  entity_type: string | null
  entity_id: string | null
  read_at: string | null
  created_at: string
}

export type NotificationTone = 'info' | 'warn' | 'success'

// Pure: type → tone for the dot/icon colour. Unknown → 'info'.
export function notificationTone(type: string): NotificationTone {
  switch (type) {
    case 'correction_approved':
      return 'success'
    // document_deletion_requested joins these: a client is waiting on a decision from us, which
    // carries the same "needs attention" weight as an error or a missing statement.
    case 'correction_rejected':
    case 'bank_missing':
    case 'document_error':
    case 'document_deletion_requested':
    case 'document_question_asked':
      return 'warn'
    default:
      return 'info'
  }
}

// --- themes ------------------------------------------------------------------------------
//
// Jorge, 2026-09-18: "aquelas não estão arrumadas por assuntos, por temas." One flat
// chronological list mixes "a client is waiting on you" with "this client is missing a bank
// statement", and at volume that is unreadable.
//
// The taxonomy is grounded in what actually reaches an operator, not in an abstract hierarchy:
// the question a theme answers is "whose problem is this and what do I do about it". It spans
// BOTH notification systems on the page — the table-backed inbox and the derived standing
// alerts — because a reader does not care which of the two produced a row.

export type NotificationTheme = 'pedidos' | 'documentos' | 'extratos' | 'correcoes' | 'outros'

/** Display order. `outros` last — it is the catch-all for types added after this list. */
export const NOTIFICATION_THEMES: NotificationTheme[] = [
  'pedidos',
  'documentos',
  'extratos',
  'correcoes',
  'outros',
]

/** Pure: notification type → theme. Unknown types fall into 'outros' rather than disappearing. */
export function notificationTheme(type: string): NotificationTheme {
  switch (type) {
    // A client is waiting on a human decision from us.
    case 'document_deletion_requested':
    case 'document_question_asked':
      return 'pedidos'
    case 'document_error':
      return 'documentos'
    case 'bank_missing':
      return 'extratos'
    case 'correction_pending':
    case 'correction_approved':
    case 'correction_rejected':
      return 'correcoes'
    default:
      return 'outros'
  }
}

/**
 * The derived standing alerts (lib/notifications.ts) use their own `kind` vocabulary, which
 * predates this taxonomy and overlaps it. Mapping them here keeps ONE set of filter chips over
 * both lists instead of two competing filters on one page.
 */
export function derivedKindTheme(kind: string): NotificationTheme {
  switch (kind) {
    case 'errors':
      return 'documentos'
    case 'bank_missing':
      return 'extratos'
    case 'under_expected':
      return 'documentos'
    default:
      return 'outros'
  }
}

// Pure: where clicking a notification goes, from its entity ref. Null = not linkable.
export function notificationLink(n: {
  entity_type: string | null
  entity_id: string | null
}): string | null {
  if (!n.entity_id) return null
  switch (n.entity_type) {
    case 'document':
      return `/operacoes/documentos?doc=${n.entity_id}`
    case 'correction_proposal':
      return '/operacoes/revisoes'
    case 'client':
      return `/operacoes/cliente/${n.entity_id}`
    default:
      return null
  }
}

// --- data: `notifications` isn't in the generated db-types yet (migration ships
// UNAPPLIED, like correction_proposals). Cast in this ONE place; drop the casts when
// the table lands in db-types. ---
type NotifQuery = {
  select: (s: string) => NotifQuery
  order: (c: string, o: { ascending: boolean }) => NotifQuery
  limit: (n: number) => Promise<{ data: unknown; error: { message: string } | null }>
}

export async function fetchNotifications(limit = 50): Promise<NotificationRow[]> {
  const q = supabase.from('notifications' as never) as unknown as NotifQuery
  const { data, error } = await q
    .select('id, type, title, body, entity_type, entity_id, read_at, created_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) return []
  return (data ?? []) as NotificationRow[]
}

// HEAD count of the caller's unread rows — drives the sidebar bell badge without pulling
// inbox rows into the layout. RLS scopes the count to the current user's own notifications.
type NotifCountQuery = {
  select: (
    s: string,
    o: { count: 'exact'; head: true },
  ) => {
    is: (col: string, val: null) => Promise<{ count: number | null; error: unknown }>
  }
}

export async function fetchUnreadNotificationsCount(): Promise<number> {
  const q = supabase.from('notifications' as never) as unknown as NotifCountQuery
  const { count, error } = await q.select('id', { count: 'exact', head: true }).is('read_at', null)
  if (error) return 0
  return count ?? 0
}

export async function markNotificationsRead(ids: string[]): Promise<{ error: string | null }> {
  if (ids.length === 0) return { error: null }
  const { error } = await supabase.rpc('mark_notifications_read' as never, { p_ids: ids } as never)
  return { error: error ? error.message : null }
}
